import type {
    VectorizeFullResult,
    VectorizeStreamEvent,
} from "~/lib/api/types/imageProcessor";

/** Ein Eintrag im Gedanken-/Werkzeugprotokoll neben der Bühne. */
export type PlaybackEntry =
    | {
          id: string;
          at: number;
          cached: boolean;
          kind: "start";
          maxIterations: number;
          provider: string;
      }
    | {
          id: string;
          at: number;
          changes: string[];
          kind: "result";
          summary: string;
      }
    | {
          id: string;
          at: number;
          durationMs?: number;
          frameIndex?: number;
          input: Record<string, unknown>;
          kind: "tool";
          message?: string;
          status: "done" | "failed" | "running";
          step: number;
          summary?: string;
          tool: string;
      }
    | {
          id: string;
          at: number;
          kind: "error";
          message: string;
      }
    | {
          id: string;
          at: number;
          kind: "step";
          step: number;
      }
    | {
          id: string;
          at: number;
          kind: "thought";
          step: number;
          text: string;
          thoughtKind: "reasoning" | "text";
      }
    | {
          id: string;
          at: number;
          kind: "upscale";
          status: "done" | "failed" | "running";
      };

/**
 * Ein Bild im Wiedergabe-Player: die Vorlage, das hochskalierte Bild, jede
 * Vorschau, die ein Werkzeug der KI erzeugt hat, und zuletzt das Ergebnis.
 */
export type PlaybackFrame = {
    at: number;
    image: string;
    kind: "result" | "source" | "tool" | "upscaled";
    step?: number;
    /** Beim Ergebnis und beim laufenden Zeichnen: das rohe SVG, damit es inline gezeigt werden kann. */
    svg?: string;
    tool?: string;
};

export type PlaybackState = {
    entries: PlaybackEntry[];
    frames: PlaybackFrame[];
    maxIterations: number;
    step: number;
};

export function addFrame(state: PlaybackState, frame: PlaybackFrame): number {
    state.frames.push(frame);

    return state.frames.length - 1;
}

export function addResult(
    state: PlaybackState,
    result: VectorizeFullResult,
    at: number,
): number {
    state.entries.push({
        id: "result",
        at,
        changes: result.changes,
        kind: "result",
        summary: result.summary,
    });

    return addFrame(state, {
        at,
        image: svgDataUrl(result.svg),
        kind: "result",
        svg: result.svg,
    });
}

/**
 * Überführt ein Stream-Ereignis des Image-Processors in Protokolleinträge und
 * Bilder. Gedankenfragmente mit derselben `id` werden zu einem Eintrag
 * zusammengefügt; Werkzeugaufrufe werden beim Eintreffen des Ergebnisses
 * aktualisiert statt doppelt aufgeführt.
 */
export function applyStreamEvent(
    state: PlaybackState,
    event: VectorizeStreamEvent,
): void {
    switch (event.type) {
        case "error":
            state.entries.push({
                id: "error",
                at: event.at,
                kind: "error",
                message: event.message,
            });

            break;

        case "result":
            addResult(state, event.result, event.at);

            break;

        case "start":
            state.maxIterations = event.maxIterations;
            state.entries.push({
                id: "start",
                at: event.at,
                cached: event.cached,
                kind: "start",
                maxIterations: event.maxIterations,
                provider: event.provider,
            });

            break;

        case "step":
            state.step = event.step;
            state.entries.push({
                id: `step-${String(event.step)}`,
                at: event.at,
                kind: "step",
                step: event.step,
            });

            break;

        case "thought": {
            const last = state.entries.at(-1);

            if (last?.kind === "thought" && last.id === `thought-${event.id}`) {
                last.text += event.text;
            } else {
                state.entries.push({
                    id: `thought-${event.id}`,
                    at: event.at,
                    kind: "thought",
                    step: event.step,
                    text: event.text,
                    thoughtKind: event.kind,
                });
            }

            break;
        }

        case "tool-call":
            state.entries.push({
                id: `tool-${event.toolCallId}`,
                at: event.at,
                input: event.input,
                kind: "tool",
                status: "running",
                step: event.step,
                tool: event.tool,
            });

            break;

        case "tool-error": {
            const entry = findTool(state, event.toolCallId, event);

            entry.message = event.message;
            entry.status = "failed";

            break;
        }

        case "tool-progress": {
            // Das Zeichenmodell liefert sein Dokument laufend: dasselbe
            // Bild wird ersetzt, damit die Formen sichtbar hinzukommen, ohne
            // die Zeitleiste mit Zwischenständen zu füllen.
            if (!event.svg) break;

            const entry = findTool(state, event.toolCallId, event);
            const frame = {
                at: event.at,
                image: svgDataUrl(event.svg),
                kind: "tool" as const,
                step: event.step,
                svg: event.svg,
                tool: event.tool,
            };

            if (entry.frameIndex === undefined) {
                entry.frameIndex = addFrame(state, frame);
            } else {
                state.frames[entry.frameIndex] = frame;
            }

            break;
        }

        case "tool-result": {
            const entry = findTool(state, event.toolCallId, event);
            const image = event.images.at(-1);

            entry.durationMs = event.durationMs;
            entry.status = "done";
            entry.summary = event.summary;

            if (image) {
                entry.frameIndex = addFrame(state, {
                    at: event.at,
                    image: `data:${image.mediaType};base64,${image.base64}`,
                    kind: "tool",
                    step: event.step,
                    tool: event.tool,
                });
            }

            break;
        }

        default:
            break;
    }
}

export function createPlaybackState(): PlaybackState {
    return { entries: [], frames: [], maxIterations: 0, step: 0 };
}

export function svgDataUrl(svg: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function findTool(
    state: PlaybackState,
    toolCallId: string,
    event: { at: number; step: number; tool: string },
): Extract<PlaybackEntry, { kind: "tool" }> {
    const id = `tool-${toolCallId}`;
    const existing = state.entries.find(
        (entry): entry is Extract<PlaybackEntry, { kind: "tool" }> =>
            entry.kind === "tool" && entry.id === id,
    );

    if (existing) return existing;

    // Ein Ergebnis ohne vorherigen Aufruf (sollte nicht vorkommen) bekommt
    // trotzdem einen Eintrag, damit nichts verloren geht.
    const created: Extract<PlaybackEntry, { kind: "tool" }> = {
        id,
        at: event.at,
        input: {},
        kind: "tool",
        status: "running",
        step: event.step,
        tool: event.tool,
    };

    state.entries.push(created);

    return created;
}
